/**
 * Sticky header scroll-gedrag.
 *
 * Toont en verbergt het sticky-header element (`[data-agora-sticky-header]`)
 * op basis van scroll-positie. Twee modi via `data-sticky-behavior`:
 *
 *  - `'threshold'` — verschijn na `threshold` px scroll, verdwijn boven
 *    die grens (forced hide voorkomt overlap met de echte header).
 *  - `'scroll_up'` — verschijn alleen wanneer de gebruiker omhoog scrollt
 *    voorbij `threshold`; verdwijn bij omlaag scrollen of bij terugkeren
 *    boven `threshold`.
 *
 * Throttle via `requestAnimationFrame` (één update per frame) en `passive`
 * scroll-listener voor browser-scrolling performance.
 *
 * Visuele animatie wordt aangedreven door `motion-utils.slideIn`/`slideOut`
 * — Motion.js handelt prefers-reduced-motion automatisch af door
 * duration op 0 te clampen, geen extra CSS-overrides nodig.
 *
 * @module modules/ui/sticky-header
 */

import { slideIn, slideOut } from './motion-utils.js';

const SELECTORS = {
    sticky: '[data-agora-sticky-header]',
};

/**
 * Public init. Idempotent via dataset-flag op het sticky element.
 * Graceful degradation: zonder sticky element in de DOM doet de functie
 * niets.
 *
 * @returns {void}
 */
export function initStickyHeader() {
    const sticky = document.querySelector(SELECTORS.sticky);
    if (!(sticky instanceof HTMLElement)) return;

    if (sticky.dataset.agoraStickyBound === '1') return;
    sticky.dataset.agoraStickyBound = '1';

    const behavior = sticky.dataset.stickyBehavior === 'scroll_up' ? 'scroll_up' : 'threshold';

    const threshold = parseSafeInt(sticky.dataset.stickyThreshold, 200);
    const scrollUpDelta = parseSafeInt(sticky.dataset.stickyScrollUp, 50);
    const scrollDownDelta = parseSafeInt(sticky.dataset.stickyScrollDown, 30);

    let lastScrollY = window.scrollY;
    let isVisible = false;
    let ticking = false;
    // Anker-positie waar de scroll-richting voor het laatst wisselde.
    // Op basis van `Math.abs(scrollY - scrollAnchor)` bepalen we of er
    // genoeg distance is overbrugd om de zichtbaarheid te toggelen.
    let scrollAnchor = window.scrollY;
    let lastDirection = null;

    /**
     * Bereken nieuwe zichtbaarheid op basis van scroll-positie en richting.
     *
     * Voor `scroll_up` mode hanteren we anti-flicker thresholds: na een
     * richtingswissel moet de gebruiker eerst `scrollUpDelta` (omhoog) of
     * `scrollDownDelta` (omlaag) pixels overbruggen voordat we de
     * zichtbaarheid wisselen. Onder die distance blijft de huidige state.
     */
    const update = () => {
        const scrollY = window.scrollY;
        const pastThreshold = scrollY > threshold;
        // Bij gelijke scrollY ontstaat een race-conditie wanneer het rAF
        // dubbel vuurt op exact dezelfde positie — geen richtingswissel,
        // dus we hergebruiken de laatste richting (default: 'down').
        const direction =
            scrollY === lastScrollY
                ? (lastDirection ?? 'down')
                : scrollY > lastScrollY
                  ? 'down'
                  : 'up';

        let shouldShow = false;

        if (behavior === 'threshold') {
            shouldShow = pastThreshold;
        } else {
            // scroll_up mode: anchor-based delta bepaling.
            if (direction !== lastDirection) {
                scrollAnchor = scrollY;
                lastDirection = direction;
            }
            const distance = Math.abs(scrollY - scrollAnchor);

            if (!pastThreshold) {
                // Boven main threshold (= terug bij top) altijd verbergen
                // — daar staat de echte header al, sticky zou overlappen.
                shouldShow = false;
            } else if (direction === 'up' && distance >= scrollUpDelta) {
                shouldShow = true;
            } else if (direction === 'down' && distance >= scrollDownDelta) {
                shouldShow = false;
            } else {
                // Distance nog te klein → geen state-flip (anti-flicker).
                shouldShow = isVisible;
            }
        }

        if (shouldShow !== isVisible) {
            isVisible = shouldShow;

            if (isVisible) {
                // slideIn zet `hidden = false` + `aria-hidden = false`
                // vóór de animatie en animeert transform van -100% naar 0.
                slideIn(sticky, 'top', { duration: 0.3 });
            } else {
                // Wanneer een open mobile-drawer de sticky-toggle in
                // `is-drawer-open` heeft staan (position: fixed), zou
                // die toggle losdrijvend in het viewport blijven hangen
                // terwijl de sticky zelf wegschuift. Reset de class zodat
                // de drawer-open hamburger terugvalt op de originele
                // toggle in `<header>` — die is nu in beeld.
                sticky
                    .querySelectorAll('[data-agora-menu-toggle].is-drawer-open')
                    .forEach((t) => t.classList.remove('is-drawer-open'));

                // slideOut animeert eerst en zet daarna `hidden = true`.
                slideOut(sticky, 'top', { duration: 0.25 });
            }
        }

        lastScrollY = scrollY;
        ticking = false;
    };

    window.addEventListener(
        'scroll',
        () => {
            if (ticking) return;
            window.requestAnimationFrame(update);
            ticking = true;
        },
        { passive: true },
    );
}

/**
 * Parse een dataset-waarde naar een non-negatief integer met fallback.
 * Niet-numerieke / negatieve waardes vallen terug op de default.
 *
 * @param {string|undefined} raw
 * @param {number}           fallback
 *
 * @returns {number}
 */
function parseSafeInt(raw, fallback) {
    const n = Number.parseInt(raw ?? '', 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
}
